import React, { useState, useEffect, useRef } from "react";
import i18n from "../../src/i18n"; 
import "./LanguageSwitcher.css";

const LANGUAGES = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "ar", name: "Arabic", nativeName: "العربية" },
  { code: "bn", name: "Bengali", nativeName: "বাংলা" },
  { code: "zh", name: "Chinese", nativeName: "中文" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी" },
  { code: "it", name: "Italian", nativeName: "Italiano" },
  { code: "id", name: "Indonesian", nativeName: "Bahasa Indonesia" },
  { code: "ko", name: "Korean", nativeName: "한국어" },
  { code: "pt", name: "Portuguese", nativeName: "Português" },
  { code: "ru", name: "Russian", nativeName: "Русский" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "sw", name: "Swahili", nativeName: "Kiswahili" },
  { code: "tr", name: "Turkish", nativeName: "Türkçe" },
  { code: "te", name: "Telugu", nativeName: "తెలుగు" },
  { code: "ta", name: "Tamil", nativeName: "தமிழ்" },
  { code: "ur", name: "Urdu", nativeName: "اردو" }
];

const LanguageSwitcher = () => {
  const [showPip, setShowPip] = useState(false);
  const [showEdgePanel, setShowEdgePanel] = useState(true);
  const [selectedLang, setSelectedLang] = useState(i18n.language || "en");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const pipRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const currentLang = i18n.language;
    if (currentLang !== selectedLang) {
      setSelectedLang(currentLang);
    }
  }, []);

  const startAutoHideTimer = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setShowPip(false);
      setShowEdgePanel(true);
      setIsDropdownOpen(false);
    }, 4000);
  };

  useEffect(() => {
    startAutoHideTimer();

    const handleClickOutside = (event) => {
      if (pipRef.current && !pipRef.current.contains(event.target)) {
        setShowPip(false);
        setShowEdgePanel(true);
        setIsDropdownOpen(false);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setSelectedLang(i18n.language);
  }, [i18n.language]);

  const handleEdgePanelClick = (e) => {
    e.stopPropagation();
    setShowPip(true);
    setShowEdgePanel(false);
    startAutoHideTimer();
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
    startAutoHideTimer();
  };

  const changeLanguage = async (lang) => {
    try {
      await i18n.changeLanguage(lang);
      setSelectedLang(lang);
      localStorage.setItem("language", lang);
      setIsDropdownOpen(false);
      startAutoHideTimer();
    } catch (error) {
      console.error("Error changing language:", error);
    }
  };

  const getCurrentLanguage = () => {
    return LANGUAGES.find(lang => lang.code === selectedLang) || LANGUAGES[0];
  };

  // Inline styles for responsive design
  const styles = {
    pipContainer: {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 1000,
      maxWidth: '200px',
      width: '90vw',
      maxHeight: '80vh',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
      border: '1px solid #e0e0e0',
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    languageContent: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    },
    languageHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      cursor: 'pointer',
      backgroundColor: '#f8f9fa',
      borderBottom: '1px solid #e9ecef',
      transition: 'background-color 0.2s ease'
    },
    languageIcon: {
      fontSize: '20px',
      marginRight: '12px',
      flexShrink: 0
    },
    currentLanguageInfo: {
      flex: 1,
      textAlign: 'left'
    },
    languageName: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '2px'
    },
    languageNative: {
      fontSize: '12px',
      color: '#666',
      fontWeight: '500'
    },
    dropdownArrow: {
      transition: 'transform 0.2s ease',
      color: '#666'
    },
    dropdownArrowOpen: {
      transform: 'rotate(180deg)'
    },
    dropdownContainer: {
      backgroundColor: '#ffffff',
      borderTop: '1px solid #e9ecef',
      maxHeight: '300px',
      overflowY: 'auto'
    },
    dropdownHeader: {
      padding: '12px 16px',
      backgroundColor: '#f8f9fa',
      borderBottom: '1px solid #e9ecef',
      fontSize: '12px',
      fontWeight: '600',
      color: '#666',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    languageList: {
      padding: '8px 0'
    },
    languageItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      border: 'none',
      backgroundColor: 'transparent',
      width: '100%',
      textAlign: 'left'
    },
    languageItemSelected: {
      backgroundColor: '#e3f2fd'
    },
    languageItemContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flex: 1
    },
    languageText: {
      flex: 1
    },
    languageCode: {
      fontSize: '11px',
      fontWeight: '600',
      color: '#999',
      backgroundColor: '#f5f5f5',
      padding: '2px 6px',
      borderRadius: '4px',
      marginLeft: '8px'
    },
    selectedIndicator: {
      color: '#1976d2',
      marginLeft: '8px'
    },
    quickActions: {
      padding: '16px',
      backgroundColor: '#f8f9fa',
      borderTop: '1px solid #e9ecef'
    },
    quickActionsLabel: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#666',
      marginBottom: '8px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    quickButtons: {
      display: 'flex',
      gap: '8px'
    },
    quickButton: {
      flex: 1,
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '6px',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    quickButtonActive: {
      backgroundColor: '#1976d2',
      color: '#ffffff',
      borderColor: '#1976d2'
    },
    edgePanel: {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 1000,
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      backgroundColor: '#ffffff23',
      color: 'white',
      border: 'none',
      cursor: 'pointer',
      fontSize: '25px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
      transition: 'all 0.3s ease'
    }
  };

  return (
    <>
      {showPip && (
        <div ref={pipRef} style={styles.pipContainer}>
          <div style={styles.languageContent}>
            {/* Header with current language */}
            <div 
              style={styles.languageHeader} 
              onClick={toggleDropdown}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            >
              <div style={styles.languageIcon}>🌐</div>
              <div style={styles.currentLanguageInfo}>
                <div style={styles.languageName}>{getCurrentLanguage().name}</div>
                <div style={styles.languageNative}>{getCurrentLanguage().nativeName}</div>
              </div>
              <div style={{
                ...styles.dropdownArrow,
                ...(isDropdownOpen ? styles.dropdownArrowOpen : {})
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            {/* Dropdown menu */}
            {isDropdownOpen && (
              <div style={styles.dropdownContainer}>
                <div style={styles.dropdownHeader}>
                  <span>Select Language</span>
                </div>
                <div style={styles.languageList}>
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      style={{
                        ...styles.languageItem,
                        ...(selectedLang === lang.code ? styles.languageItemSelected : {})
                      }}
                      onClick={() => changeLanguage(lang.code)}
                      onMouseEnter={(e) => {
                        if (selectedLang !== lang.code) {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedLang !== lang.code) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div style={styles.languageItemContent}>
                        <div style={styles.languageText}>
                          <div style={styles.languageName}>{lang.name}</div>
                          <div style={styles.languageNative}>{lang.nativeName}</div>
                        </div>
                        <div style={styles.languageCode}>{lang.code.toUpperCase()}</div>
                      </div>
                      {selectedLang === lang.code && (
                        <div style={styles.selectedIndicator}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            
          </div>
        </div>
      )}

      {showEdgePanel && (
        <button 
          style={styles.edgePanel}
          onClick={handleEdgePanelClick}
          
        >
          🌐
        </button>
      )}
    </>
  );
};

export default LanguageSwitcher;